import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Loader2, AlertCircle, Github, Info, HardDriveDownload, RefreshCw } from 'lucide-react';
import JSZip from 'jszip';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from './utils';

const GitHubImageGallery = () => {
  const [images, setImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const observerTarget = useRef(null);

  const GITHUB_CONFIG = {
    owner: 'burner-rd',
    repo: 'nothing-sprites',
    branch: 'main',
    folderPath: 'png',
    baseUrl: 'https://api.github.com',
    rawBaseUrl: 'https://raw.githubusercontent.com',
    accessToken: null,
  };

  const CACHE_KEY = 'nothing_sprites_cache';
  const CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1 Hour

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(() => {
    if (displayedImages.length < images.length) {
      const nextPage = page + 1;
      setPage(nextPage);
      setDisplayedImages(images.slice(0, nextPage * ITEMS_PER_PAGE));
    }
  }, [page, images, displayedImages.length]);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore]); // Re-bind when loadMore changes

  const fetchImages = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // 1. Check Cache
      if (!forceRefresh) {
        const cachedData = sessionStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { timestamp, data } = JSON.parse(cachedData);
          if (Date.now() - timestamp < CACHE_EXPIRY_MS) {
            console.log('📦 Loaded metadata from session cache.');
            setImages(data);
            setDisplayedImages(data.slice(0, ITEMS_PER_PAGE));
            setPage(1);
            setLoading(false);
            return;
          }
        }
      }

      // 2. Fetch via Git Tree API (recursive)
      const headers = {
        Accept: 'application/vnd.github.v3+json',
      };
      if (GITHUB_CONFIG.accessToken) {
        headers.Authorization = `token ${GITHUB_CONFIG.accessToken}`;
      }

      // recursive=1 brings back the entire repo map instantly
      const apiUrl = `${GITHUB_CONFIG.baseUrl}/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/git/trees/${GITHUB_CONFIG.branch}?recursive=1`;
      const response = await fetch(apiUrl, { headers });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Repository or branch not found.');
        } else if (response.status === 403 || response.status === 429) {
          throw new Error('Something Went Wrong! (GitHub API Rate Limit usually).');
        }
        throw new Error(`Fetch failed: ${response.statusText}`);
      }

      const treeData = await response.json();

      // 3. Filter and parse the tree 
      let pngFiles = treeData.tree
        .filter(item =>
          item.type === 'blob' &&
          item.path.startsWith(`${GITHUB_CONFIG.folderPath}/`) &&
          item.path.toLowerCase().endsWith('.png')
        )
        .map(item => {
          // Extract just the filename from the path
          const filename = item.path.split('/').pop();
          const match = filename.match(/Sprite \((\d+)\)\.png/i);
          const num = match ? parseInt(match[1], 10) : 0;
          const customName = match ? `Asset #${String(num).padStart(3, '0')}` : filename.replace('.png', '');

          // Construct the raw download URL manually (since trees API doesn't provide it)
          const rawUrl = `${GITHUB_CONFIG.rawBaseUrl}/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${GITHUB_CONFIG.branch}/${item.path}`;

          return {
            id: item.sha,
            originalName: filename,
            name: customName,
            downloadName: `${customName}.png`,
            sortNum: num,
            path: item.path,
            downloadUrl: rawUrl,
            displayUrl: rawUrl,
          };
        })
        .sort((a, b) => a.sortNum - b.sortNum);

      if (pngFiles.length === 0) {
        throw new Error('No PNG files found!.');
      }

      // Assuming user wants them reversed (newest/highest number first based on original code)
      pngFiles = pngFiles.reverse();

      // 4. Update State
      setImages(pngFiles);
      setDisplayedImages(pngFiles.slice(0, ITEMS_PER_PAGE));
      setPage(1);

      // 5. Save to Cache
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        data: pngFiles
      }));
      console.log(`🌐 Fetched ${pngFiles.length} items from GitHub api and cached.`);

    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = () => {
    sessionStorage.removeItem(CACHE_KEY);
    fetchImages(true);
  };

  const downloadImage = async (image) => {
    try {
      const headers = {};
      if (GITHUB_CONFIG.accessToken) {
        headers['Authorization'] = `Bearer ${GITHUB_CONFIG.accessToken}`;
      }

      const response = await fetch(image.downloadUrl, { headers });
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = image.downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Try again.');
    }
  };

  const downloadAllImages = async () => {
    if (images.length === 0) return;

    setDownloadingAll(true);
    setDownloadProgress({ current: 0, total: images.length });
    const zip = new JSZip();

    try {
      // BATCHING LOGIC: Process 5 images concurrently to avoid network/memory crash
      const BATCH_SIZE = 5;
      for (let i = 0; i < images.length; i += BATCH_SIZE) {
        const batch = images.slice(i, i + BATCH_SIZE);

        // Wait for current batch to finish
        await Promise.all(batch.map(async (image) => {
          try {
            const response = await fetch(image.downloadUrl);
            if (!response.ok) {
              console.warn(`Skipping failed download: ${image.originalName}`);
              return;
            }
            const blob = await response.blob();
            zip.file(image.downloadName, blob);
          } catch (e) {
            console.warn(`Failed specifically on ${image.originalName}`, e);
          }
        }));

        // Update UI progress safely
        setDownloadProgress(prev => ({ ...prev, current: Math.min(i + BATCH_SIZE, images.length) }));
      }

      setDownloadProgress(prev => ({ ...prev, current: 'Zipping...' }));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sprites.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading all images as ZIP:', err);
      alert('Failed to download all images. Please try again.');
    } finally {
      setDownloadingAll(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // State Overlays Container
  const StateOverlay = ({ children, isError }) => (
    <div className="relative min-h-[50vh] flex flex-col items-center justify-center p-8 z-10 w-full">
      <div className="absolute inset-0 bg-blue-500/[0.02] dark:bg-blue-500/[0.05] rounded-[3rem] blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "glass p-12 rounded-[2rem] text-center max-w-lg mx-auto relative overflow-hidden group border",
          isError ? "border-red-500/20 bg-red-500/5" : "border-white/5"
        )}
      >
        {children}
      </motion.div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-[#0B0F19] text-gray-100 selection:bg-blue-500/30 font-sans overflow-x-hidden">

      {/* Background Ambience */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[-10%] sm:top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-purple-600/10 rounded-full blur-[150px]" />
      </div>

      {/* Dynamic Header */}
      <nav className="sticky top-0 z-50 w-full border-b border-white/5 bg-[#0B0F19]/60 backdrop-blur-xl supports-[backdrop-filter]:bg-[#0B0F19]/40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4 truncate">
            <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-white/10 shadow-inner">
              <Github className="w-5 h-5 text-gray-300" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-semibold tracking-tight text-white mb-0.5 truncate">Nothing Sprites</h1>
              <div className="flex items-center gap-2 text-[10px] sm:text-xs font-medium text-gray-400">
                <span className="flex-shrink-0 flex h-2 w-2 rounded-full bg-emerald-500/20 items-center justify-center">
                  <span className="h-1 w-1 rounded-full bg-emerald-400"></span>
                </span>
                <span className="truncate">{images.length > 0 ? `${images.length} assets mapped` : 'Connecting...'}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Refresh Cache Button */}
            <button
              onClick={handleForceRefresh}
              disabled={loading || downloadingAll}
              title="Force clear cache and fetch from GitHub"
              className="p-1.5 sm:p-2.5 text-gray-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </button>

            {/* Batch Zip Button */}
            {images.length > 0 && (
              <button
                onClick={downloadAllImages}
                disabled={downloadingAll}
                className="group relative inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-white text-black font-semibold rounded-lg sm:rounded-xl hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-base whitespace-nowrap overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-100/0 via-blue-100/50 to-blue-100/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                {downloadingAll ? (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin flex-shrink-0" />
                ) : (
                  <HardDriveDownload className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                )}
                <span>
                  {downloadingAll
                    ? (typeof downloadProgress.current === 'number' ? `${downloadProgress.current}/${downloadProgress.total}` : downloadProgress.current)
                    : 'Download All'
                  }
                </span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="relative z-10 max-w-[1400px] mx-auto px-6 py-12 pb-32">

        {loading ? (
          <StateOverlay>
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-500/20 rounded-full blur-xl animate-pulse" />
                <Loader2 className="h-10 w-10 text-white animate-spin relative" />
              </div>
            </div>
            <h2 className="text-xl font-medium text-white mb-2">Syncing Repository Tree</h2>
            <p className="text-gray-400 text-sm">Fetching structural blueprint from GitHub...</p>
          </StateOverlay>
        ) : error ? (
          <StateOverlay isError>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center border border-red-500/20">
                <AlertCircle className="h-8 w-8 text-red-400" />
              </div>
            </div>
            <h2 className="text-xl font-medium text-white mb-3">Connection Failed</h2>
            <p className="text-gray-400 text-sm mb-8 leading-relaxed">
              {error}
            </p>
            <button
              onClick={() => handleForceRefresh()}
              className="inline-flex items-center justify-center px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-sm font-medium transition-colors w-full"
            >
              Try Again
            </button>
          </StateOverlay>
        ) : images.length === 0 ? (
          <StateOverlay>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-800 flex items-center justify-center border border-gray-700">
                <Info className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <h2 className="text-xl font-medium text-white mb-2">No Sprites Found</h2>
            <p className="text-gray-400 text-sm">The target folder appears to be empty or contains no PNG files.</p>
          </StateOverlay>
        ) : (
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5"
            >
              <AnimatePresence>
                {displayedImages.map((image, i) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="group relative flex flex-col bg-gray-900/40 border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 hover:bg-gray-800/60 transition-all duration-300"
                  >
                    {/* Image Container */}
                    <div className="relative aspect-square p-6 flex items-center justify-center bg-black/20">
                      <img
                        src={image.displayUrl}
                        alt={image.name || 'GitHub image'}
                        className="max-w-full max-h-full object-contain filter group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMjIyIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZpbGw9IiM1NTUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkZhaWxlZDwvdGV4dD48L3N2Zz4=';
                        }}
                      />

                      {/* Hover Overlay Action */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                        <button
                          onClick={() => downloadImage(image)}
                          className="p-3 bg-white text-black rounded-full transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 hover:scale-110 shadow-lg"
                          title="Download Sprite"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="p-4 border-t border-white/5">
                      <p className="text-xs font-medium text-gray-300 truncate" title={image.name}>
                        {image.name}
                      </p>
                      <p className="text-[10px] text-gray-600 mt-1 uppercase tracking-wider font-semibold">PNG Image</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Infinite Scroll trigger target */}
            {displayedImages.length < images.length && (
              <div ref={observerTarget} className="w-full flex justify-center py-8">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  <span className="text-sm text-gray-400 font-medium">Loading more assets...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

    </div>
  );
};

export default GitHubImageGallery;
